import {
  certifyCommandCenterCompletion,
} from '@/governor/system-workspaces/LegacyCommandCenterCompletionCertificationEngine';

function statusClass(
  status: string,
): string {
  if (
    status === 'PASS' ||
    status === 'COMPLETE' ||
    status === 'ALL_ELIGIBLE'
  ) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-900';
  }

  if (
    status === 'BLOCKED'
  ) {
    return 'border-rose-200 bg-rose-50 text-rose-900';
  }

  return 'border-orange-200 bg-orange-50 text-orange-900';
}

export default function LegacyCommandCenterCompletionCertification() {
  const certification =
    certifyCommandCenterCompletion();

  return (
    <section className="my-6 rounded-[2rem] border border-violet-200 bg-[#f7f3ff] p-5 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-700">
            CC-SYS-001H
          </p>

          <h2 className="mt-2 text-3xl font-bold text-slate-950">
            Completion & Non-Duplication Certification
          </h2>

          <p className="mt-2 max-w-3xl text-slate-700">
            Final certification of workspace registration,
            legacy ownership, duplicate authority, evidence
            preservation, and retirement readiness.
          </p>
        </div>

        <span
          className={`rounded-full border px-4 py-2 text-sm font-bold ${statusClass(
            certification.migrationStatus,
          )}`}
        >
          {certification.migrationStatus}
        </span>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div
          className={`rounded-xl border p-4 ${statusClass(
            certification.foundationStatus,
          )}`}
        >
          <p className="text-xs font-bold uppercase">
            Foundation
          </p>
          <p className="mt-1 text-xl font-bold">
            {certification.foundationStatus}
          </p>
        </div>

        <div
          className={`rounded-xl border p-4 ${statusClass(
            certification.nonDuplicationStatus,
          )}`}
        >
          <p className="text-xs font-bold uppercase">
            Non-duplication
          </p>
          <p className="mt-1 text-xl font-bold">
            {
              certification
                .nonDuplicationStatus
            }
          </p>
        </div>

        <div
          className={`rounded-xl border p-4 ${statusClass(
            certification.retirementStatus,
          )}`}
        >
          <p className="text-xs font-bold uppercase">
            Retirement
          </p>
          <p className="mt-1 text-xl font-bold">
            {
              certification
                .retirementStatus
                .replaceAll('_', ' ')
            }
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-bold uppercase text-slate-500">
            Completion
          </p>
          <p className="mt-1 text-xl font-bold text-slate-950">
            {
              certification
                .completionPercentage
            }
            %
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl bg-white p-4">
          <p className="text-xs font-bold uppercase text-slate-500">
            Legacy surfaces
          </p>
          <p className="mt-1 text-2xl font-bold">
            {
              certification
                .totalLegacySurfaces
            }
          </p>
        </div>

        <div className="rounded-xl bg-white p-4">
          <p className="text-xs font-bold uppercase text-slate-500">
            Governed owners
          </p>
          <p className="mt-1 text-2xl font-bold">
            {
              certification
                .governedOwnerCount
            }
          </p>
        </div>

        <div className="rounded-xl bg-white p-4">
          <p className="text-xs font-bold uppercase text-slate-500">
            Registered workspaces
          </p>
          <p className="mt-1 text-2xl font-bold">
            {
              certification
                .registeredWorkspaceCount
            }
          </p>
        </div>

        <div className="rounded-xl bg-white p-4">
          <p className="text-xs font-bold uppercase text-slate-500">
            Retirement eligible
          </p>
          <p className="mt-1 text-2xl font-bold">
            {
              certification
                .retirementEligibleCount
            }
          </p>
        </div>
      </div>

      {certification.globalBlockers.length > 0 ? (
        <div className="mt-5 rounded-2xl border border-orange-200 bg-orange-50 p-4">
          <p className="font-bold text-orange-950">
            Completion blockers
          </p>

          <div className="mt-3 space-y-2">
            {certification.globalBlockers.map(
              blocker => (
                <p
                  key={blocker}
                  className="text-sm text-orange-900"
                >
                  • {blocker}
                </p>
              ),
            )}
          </div>
        </div>
      ) : null}

      {certification
        .unregisteredOwningSystems
        .length > 0 ? (
        <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <p className="font-bold text-rose-950">
            Missing governed workspaces
          </p>

          <p className="mt-2 text-sm text-rose-900">
            {certification
              .unregisteredOwningSystems
              .join(', ')}
          </p>
        </div>
      ) : null}

      <div className="mt-5 grid gap-3 xl:grid-cols-2">
        {certification.workspaceRecords.map(
          record => (
            <article
              key={record.systemId}
              className="rounded-2xl border border-violet-200 bg-white p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-bold text-slate-950">
                    {record.systemId}
                  </p>

                  <p className="mt-1 text-sm text-slate-600">
                    {
                      record.legacySurfaceCount
                    }{' '}
                    legacy surfaces
                  </p>
                </div>

                <span
                  className={`rounded-full border px-3 py-1 text-xs font-bold uppercase ${statusClass(
                    record.completionState ===
                      'complete'
                      ? 'PASS'
                      : record.completionState ===
                          'not_started'
                        ? 'BLOCKED'
                        : 'IN_PROGRESS',
                  )}`}
                >
                  {record.completionState.replaceAll(
                    '_',
                    ' ',
                  )}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-700">
                <p>
                  <b>Registered:</b>{' '}
                  {String(
                    record.workspaceRegistered,
                  )}
                </p>

                <p>
                  <b>Mapped:</b>{' '}
                  {record.mappedSurfaceCount}/
                  {record.legacySurfaceCount}
                </p>

                <p>
                  <b>Projected:</b>{' '}
                  {
                    record
                      .projectedSurfaceCount
                  }
                </p>

                <p>
                  <b>Certified:</b>{' '}
                  {
                    record
                      .certifiedSurfaceCount
                  }
                </p>

                <p>
                  <b>Scenarios:</b>{' '}
                  {record.scenarioCount}
                </p>

                <p>
                  <b>Repairs:</b>{' '}
                  {record.repairCount}
                </p>
              </div>

              {record.blockers.length > 0 ? (
                <p className="mt-3 text-sm text-slate-600">
                  {record.blockers.join(' ')}
                </p>
              ) : null}
            </article>
          ),
        )}
      </div>

      <div className="mt-5 rounded-xl border border-violet-200 bg-white p-4 text-sm text-slate-700">
        <b>Certification decision:</b> mapping is established and
        duplicate authority is blocked, but no legacy surface may
        retire until its owning workspace and governed replacement
        are registered, projected, and certified.
      </div>
    </section>
  );
}
